import { useEffect, useState } from "react";
import { Bell, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { useStore } from "../../context/StoreContext";

export const LaunchSignup = ({ dark = true, testid = "launch-signup" }) => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try { await api.post("/launch-signup", { email, lang }); setState("done"); }
    catch (err) { toast.error(errMsg(err)); setState("idle"); }
  };
  if (state === "done") return <p data-testid={`${testid}-success`} className={`flex items-center gap-2 text-sm ${dark ? "text-[#C9B6F2]" : "text-[#6E3FB8]"}`}><Check className="h-4 w-4 text-[#F2C230]" />{t("notify_ok")}</p>;
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md" data-testid={testid}>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} data-testid={`${testid}-email`}
        className={`flex-1 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#F2C230] border transition-colors ${dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/40" : "bg-white border-[#120E1F]/10"}`} />
      <button type="submit" disabled={state === "loading"} className="btn-gold whitespace-nowrap shrink-0" data-testid={`${testid}-submit`}>
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}{t("notify_cta")}
      </button>
    </form>
  );
};

const left = (date) => Math.max(0, new Date(date).getTime() - Date.now());

export const Countdown = () => {
  const { launch_date } = useStore();
  const { t } = useLang();
  const [ms, setMs] = useState(() => (launch_date ? left(launch_date) : 0));
  useEffect(() => {
    if (!launch_date) return undefined;
    setMs(left(launch_date));
    const id = setInterval(() => setMs(left(launch_date)), 1000);
    return () => clearInterval(id);
  }, [launch_date]);
  if (!launch_date || ms <= 0) return null;
  const parts = [[Math.floor(ms / 864e5), "cd_d"], [Math.floor(ms / 36e5) % 24, "cd_h"], [Math.floor(ms / 6e4) % 60, "cd_m"], [Math.floor(ms / 1e3) % 60, "cd_s"]];
  return (
    <div className="flex gap-3" data-testid="launch-countdown">
      {parts.map(([v, k]) => (
        <div key={k} className="w-16 sm:w-20 rounded-2xl bg-white/5 border border-[#8A5CD6]/40 py-3 text-center">
          <p className="font-mono text-2xl sm:text-3xl text-[#F2C230]">{String(v).padStart(2, "0")}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{t(k)}</p>
        </div>
      ))}
    </div>
  );
};

export const LaunchSection = () => {
  const { t } = useLang();
  const { prelaunch } = useStore();
  if (!prelaunch) return null;
  return (
    <section id="launch" className="bg-[#120E1F] grain relative overflow-hidden" data-testid="launch-section">
      <div className="psychic-glow absolute -left-20 -top-24 h-96 w-96 rounded-full" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-5">
          <p className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">{t("launch_kicker")}</p>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">{t("launch_title")}</h2>
          <p className="text-white/60 leading-relaxed max-w-lg">{t("launch_text")}</p>
        </div>
        <div className="space-y-6"><Countdown /><LaunchSignup /></div>
      </div>
    </section>
  );
};
